import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import {
  CheckCircle2,
  Crown,
  Layers,
  ListOrdered,
  RefreshCw,
  Swords,
  Trophy,
} from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useGetLinkQuery,
  useLazyStartLichessLinkQuery,
  useUnlinkLichessMutation,
} from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { cn } from '@/lib/cn';

/**
 * QChess home — entry point for the chess surface.
 *
 * If the user hasn't linked a Lichess account: shows a "Link Lichess" CTA that
 * fetches the OAuth authorize URL (`GET /v1/link/lichess/start`) and opens it
 * in the system browser via Telegram `openLink`. After the user consents on
 * Lichess, the browser hits the qchess callback and the account is linked; the
 * user comes back and taps "refresh" to re-fetch `GET /v1/link`.
 *
 * If linked: shows the Lichess username + an unlink action, plus quick-nav
 * cards to Tournaments / Duels / Leaderboard.
 */
export function Chess() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const linkQuery = useGetLinkQuery(undefined, { refetchOnMountOrArgChange: true });
  const { refetch: refetchLink } = linkQuery;
  const [startLink, { isFetching: starting }] = useLazyStartLichessLinkQuery();
  const [unlink, { isLoading: unlinking }] = useUnlinkLichessMutation();
  const [linkError, setLinkError] = useState<string | null>(null);

  // Re-check link status whenever the mini app becomes visible again — e.g.
  // after the user finishes the Lichess OAuth in the system browser and bounces
  // back to Telegram. The account is persisted server-side before the return,
  // so this flips the UI to "linked" automatically, without a manual refresh.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetchLink();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetchLink]);

  const linked = linkQuery.data?.linked ?? false;
  const username = linkQuery.data?.lichessUsername;

  const onLink = async () => {
    haptics.impact('medium');
    setLinkError(null);
    try {
      const res = await startLink().unwrap();
      // openLink hands the https authorize URL to Telegram → system browser,
      // where Lichess OAuth isn't blocked (unlike inside the WebView).
      openLink.ifAvailable(res.authorizeUrl);
    } catch (err) {
      haptics.notification('error');
      setLinkError(t(qchessErrorKey(classifyQchessError(err))));
    }
  };

  const onUnlink = async () => {
    haptics.selection();
    try {
      await unlink().unwrap();
      haptics.notification('success');
    } catch {
      haptics.notification('error');
    }
  };

  return (
    <div className="flex flex-col gap-5 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Crown aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('home.title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('home.subtitle')}</p>
      </header>

      {linkQuery.isLoading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader />
        </div>
      ) : linked ? (
        /* Linked: account card */
        <section className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15 text-success">
            <CheckCircle2 aria-hidden className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
              {t('home.linkedAs')}
            </p>
            <p className="truncate text-sm font-semibold text-fg">{username}</p>
          </div>
          <button
            type="button"
            onClick={() => void onUnlink()}
            disabled={unlinking}
            className="shrink-0 rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-muted transition-opacity active:opacity-80 disabled:opacity-50"
          >
            {unlinking ? t('home.unlinking') : t('home.unlink')}
          </button>
        </section>
      ) : (
        /* Not linked: CTA */
        <section className="flex flex-col gap-3 rounded-xl bg-bg-secondary p-4">
          <p className="text-sm text-fg-muted">{t('home.linkPrompt')}</p>
          <button
            type="button"
            onClick={() => void onLink()}
            disabled={starting}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
          >
            <Crown aria-hidden className="h-4 w-4" />
            {starting ? t('home.linkOpening') : t('home.linkCta')}
          </button>
          <p className="text-[11px] text-fg-muted">{t('home.linkAfterConsent')}</p>
          <button
            type="button"
            onClick={() => {
              haptics.selection();
              void linkQuery.refetch();
            }}
            className="flex items-center justify-center gap-2 self-start text-xs font-semibold text-brand"
          >
            <RefreshCw aria-hidden className="h-3.5 w-3.5" />
            {t('home.refresh')}
          </button>
          {linkError && (
            <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
              {linkError}
            </p>
          )}
        </section>
      )}

      {/* Quick-nav cards — always reachable; gated screens handle the
          "link first" state themselves on entry. */}
      <nav className="flex flex-col gap-3">
        <NavCard
          to="/chess/tournaments"
          icon={<Trophy aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.tournaments')}
          subtitle={t('home.nav.tournamentsSub')}
        />
        <NavCard
          to="/chess/brackets"
          icon={<Layers aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.brackets')}
          subtitle={t('home.nav.bracketsSub')}
        />
        <NavCard
          to="/chess/challenges"
          icon={<Swords aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.challenges')}
          subtitle={t('home.nav.challengesSub')}
        />
        <NavCard
          to="/chess/leaderboard"
          icon={<ListOrdered aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
          title={t('home.nav.leaderboard')}
          subtitle={t('home.nav.leaderboardSub')}
        />
      </nav>

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

function NavCard({
  to,
  icon,
  title,
  subtitle,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
}) {
  const haptics = useHaptics();
  return (
    <Link
      to={to}
      onClick={() => haptics.impact('light')}
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-brand/40 bg-brand/5 px-4 py-3 text-sm',
        'transition-opacity active:opacity-80',
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
        {icon}
      </span>
      <div className="flex flex-col">
        <span className="font-bold text-fg">{title}</span>
        <span className="text-[11px] text-fg-muted">{subtitle}</span>
      </div>
    </Link>
  );
}
